import { useState, type ReactNode } from 'react';
import { downloadCsv, fileStamp } from '../lib/download';
import { num, pct, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';
import { DataTable, Segmented, Stat, StatusChip } from './data';
import { ErrorBanner, Loading } from './ui';

/**
 * A scorecard report — the library items for one team (Settings › Scorecard
 * Items), with the figure a connected system can give for each
 * (api/src/lib/scorecardReport.ts). The NOC, CS and Service Delivery pages each show one.
 *
 * The rows are the library's, so a goal set there shows here, and an item no
 * system records is still listed, saying so, rather than left off: the
 * scorecard is everything the team answers for, not only what a computer can
 * count.
 *
 * Every figure carries what was counted beside it. Several are stand-ins, and
 * a number read without its basis would claim more than it knows.
 */

export interface ScorecardLine {
  id: number;
  category: string | null;
  name: string;
  description: string | null;
  unit: '#' | '$' | '%';
  goal: number | null;
  comparison: '>=' | '<=' | '=';
  cadence: string | null;
  source: string | null;
  status: 'measured' | 'empty' | 'unavailable' | 'manual';
  value: number | null;
  /** What was counted, or why there is no figure. */
  basis: string;
  onTrack: boolean | null;
}

export interface ScorecardReportData {
  readAt: string;
  period: { from: string; to: string };
  sources: Record<string, { ok: boolean; error: string | null }>;
  lines: ScorecardLine[];
}

const PERIODS = { 7: 'Last 7 days', 30: 'Last 30 days', 90: 'Last 90 days' } as const;
type PeriodId = '7' | '30' | '90';

function figure(value: number | null, unit: ScorecardLine['unit']): string {
  if (value === null) return '—';
  if (unit === '%') return pct(value, 1);
  if (unit === '$') return `$${num(value)}`;
  return num(value, Number.isInteger(value) ? 0 : 1);
}

const goalText = (l: ScorecardLine) => (l.goal === null ? 'Not set' : `${l.comparison === '=' ? '' : `${l.comparison} `}${figure(l.goal, l.unit)}`);

function statusChip(l: ScorecardLine) {
  if (l.status === 'manual') return <StatusChip>Enter by hand</StatusChip>;
  if (l.status === 'unavailable') return <StatusChip band="serious">{l.source} not read</StatusChip>;
  if (l.status === 'empty') return <StatusChip>Nothing to count</StatusChip>;
  if (l.onTrack === true) return <StatusChip band="good">On track</StatusChip>;
  if (l.onTrack === false) return <StatusChip band="critical">Off track</StatusChip>;
  return <StatusChip band="neutral">Measured</StatusChip>;
}

export function ScorecardReport({
  path,
  team,
  filePrefix,
  caption,
}: {
  /** The API endpoint, e.g. `/noc/scorecard`. */
  path: string;
  /** How the team's items are described in empty and summary text. */
  team: string;
  filePrefix: string;
  /** Where the figures come from, for the period shown. */
  caption: (from: string, to: string) => ReactNode;
}) {
  const [period, setPeriod] = useState<PeriodId>('7');
  const to = todayLocal();
  const from = toLocalDateString(new Date(Date.now() - (Number(period) - 1) * 86_400_000));
  const live = useHeldResource<ScorecardReportData>(`${path}?${new URLSearchParams({ from, to })}`);
  const data = live.data;
  const categories = new Set(data?.lines.map((l) => l.category ?? ''));

  const controls = (
    <section className="card mb-4">
      <div className="card-body">
        <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-4)' }}>
          <Segmented
            label="Period"
            value={period}
            onChange={setPeriod}
            options={(Object.keys(PERIODS) as PeriodId[]).map((id) => ({ id, label: PERIODS[id] }))}
          />
          <button type="button" className="btn btn-ghost btn-sm" disabled={live.loading || live.refreshing} onClick={live.refresh}>
            {live.refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          {data && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() =>
                downloadCsv(`${filePrefix}-${fileStamp()}.csv`, [
                  ['Category', 'Item', 'Value', 'Goal', 'Status', 'Source', 'How it was measured', 'Period from', 'Period to'],
                  ...data.lines.map((l) => [
                    l.category ?? '',
                    l.name,
                    l.value ?? '',
                    l.goal === null ? '' : goalText(l),
                    l.status === 'measured' ? (l.onTrack === null ? 'Measured' : l.onTrack ? 'On track' : 'Off track') : l.status,
                    l.source ?? '',
                    l.basis,
                    data.period.from,
                    data.period.to,
                  ]),
                ])
              }
            >
              Download CSV
            </button>
          )}
        </div>
        <p className="xs muted mt-2">
          The {team} items from Settings › Scorecard Items. {caption(from, to)} Goals are set on the Settings screen.
          {live.fetchedAt &&
            ` As of ${new Date(live.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
        </p>
      </div>
    </section>
  );

  if (live.loading && !data) return <>{controls}<Loading label="Reading the connected systems — this can take a minute" /></>;
  if (live.error) return <>{controls}<ErrorBanner message={live.error} /></>;
  if (!data) return controls;

  const lines = data.lines;
  const measured = lines.filter((l) => l.status === 'measured');
  const off = measured.filter((l) => l.onTrack === false).length;
  const withGoal = measured.filter((l) => l.onTrack !== null).length;
  const missing = Object.keys(data.sources).filter((s) => !data.sources[s]!.ok);

  return (
    <div className="noc-page">
      {controls}

      {missing.map((s) => (
        <ErrorBanner key={s} message={`${s} could not be read, so its items have no figure: ${data.sources[s]!.error}`} />
      ))}

      {lines.length === 0 ? (
        <section className="card">
          <div className="card-body">
            <p className="sub">There are no active {team} items in the scorecard library. Add them under Settings › Scorecard Items.</p>
          </div>
        </section>
      ) : (
        <>
          <div className="grid grid-4">
            <Stat label="Items" value={num(lines.length)} foot={`${team} items in the library`} />
            <Stat label="Measured" value={num(measured.length)} foot={`With a figure from ${Object.keys(data.sources).join(' or ')}`} />
            <Stat label="Off track" value={withGoal ? num(off) : '—'} foot={withGoal ? `Of ${num(withGoal)} with a goal` : 'No goals set yet'} flagged={off > 0} />
            <Stat label="Enter by hand" value={num(lines.filter((l) => l.status === 'manual').length)} foot="No connected system records these" />
          </div>

          <section className="card">
            <div className="card-body">
              <DataTable
                rowKey={(l) => String(l.id)}
                rows={lines}
                columns={[
                  {
                    key: 'name',
                    label: 'Item',
                    render: (l) => (
                      <>
                        {categories.size > 1 && l.category && <div className="xs muted">{l.category}</div>}
                        <strong>{l.name}</strong>
                        {l.description && <div className="xs muted">{l.description}</div>}
                      </>
                    ),
                  },
                  { key: 'value', label: 'Value', align: 'right', render: (l) => <strong>{figure(l.value, l.unit)}</strong> },
                  { key: 'goal', label: 'Goal', align: 'right', render: goalText },
                  { key: 'status', label: 'Status', render: statusChip },
                  { key: 'source', label: 'Source', render: (l) => l.source ?? '—' },
                  { key: 'basis', label: 'How it was measured', render: (l) => <span className="xs">{l.basis}</span> },
                ]}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
